"use client";

import { useState, useEffect, useRef, type CSSProperties } from "react";
import Link from "next/link";
import Image from "next/image";
import { BellRing, Play, Smartphone, X } from "lucide-react";
import { ContactModal } from "@/components/ui/contact-modal";
import { GlitterParticles } from "@/components/ui/glitter-particles";
import { ScrollReveal } from "@/components/ui/scroll-reveal";
import { AnalysisPlayback } from "./_components/analysis-playback";
import { BeforeAfterGallery } from "./_components/before-after-gallery";
import { FeatureGallery } from "./_components/feature-gallery";
import { FocusGallery } from "./_components/focus-gallery";
import { SampleReportPreview } from "./_components/sample-report-preview";
import { StarWarsIntro } from "./_components/star-wars-intro";
import { IntegrationsSection } from "./_components/integrations-section";
import { UndercoverAgentsSection } from "./_components/undercover-agents-section";
import { WebsiteCtaLink } from "./_components/website-cta-link";
import { websiteBodyText, websiteEyebrowText, websiteHeroTitleText, websiteSectionTitleText } from "./_components/website-type-scale";

const taglineVariants = [
  [
    { highlight: "Catch revenue", rest: " leaks" },
    { highlight: "Focus on", rest: " what matters" },
    { highlight: "Act with", rest: " confidence" },
    { highlight: "No setup", rest: " required" },
  ],
];

const heroTitle = "Lead the market with";
const heroSignature = "AI for modern eCommerce";
const heroSubtext = "Storita helps you spot what changed, what needs action, and where to focus next.";

const signalFoundationCards = [
  {
    id: "channels",
    eyebrow: "Every channel",
    title: "One connected picture",
    description: "Ads, analytics, storefront activity, and campaigns stay in one live view.",
    chips: ["Ads", "Analytics", "Storefront"],
    cardClass: "border-amber-300/16 bg-[radial-gradient(circle_at_top,rgba(251,191,36,0.22),transparent_34%),linear-gradient(180deg,rgba(36,24,10,0.98),rgba(15,23,42,0.96))]",
    glowClass: "bg-amber-300/18",
  },
  {
    id: "ai-discovery",
    eyebrow: "AI discovery",
    title: "See new demand early",
    description: "Track AI search, referral shifts, and emerging discovery patterns before they become obvious.",
    chips: ["AI Search", "Referrals", "Demand"],
    cardClass: "border-fuchsia-300/16 bg-[radial-gradient(circle_at_top,rgba(217,70,239,0.2),transparent_34%),linear-gradient(180deg,rgba(39,14,43,0.98),rgba(15,23,42,0.96))]",
    glowClass: "bg-fuchsia-300/18",
  },
  {
    id: "conversations",
    eyebrow: "Customer signals",
    title: "Follow the conversation",
    description: "WhatsApp, email, and support questions reveal what customers need right now.",
    chips: ["WhatsApp", "Email", "Support"],
    cardClass: "border-emerald-300/16 bg-[radial-gradient(circle_at_top,rgba(52,211,153,0.2),transparent_34%),linear-gradient(180deg,rgba(14,37,31,0.98),rgba(15,23,42,0.96))]",
    glowClass: "bg-emerald-300/18",
  },
  {
    id: "market-watch",
    eyebrow: "Market watch",
    title: "Catch shifts around you",
    description: "Competitors, pricing changes, and channel movement surface before they cost you.",
    chips: ["Competitors", "Pricing", "Movement"],
    cardClass: "border-cyan-300/16 bg-[radial-gradient(circle_at_top,rgba(34,211,238,0.2),transparent_34%),linear-gradient(180deg,rgba(11,31,43,0.98),rgba(15,23,42,0.96))]",
    glowClass: "bg-cyan-300/18",
  },
];

const audienceAvatars = [
  {
    src: "/website/images/audience-avatars/person1.png",
    alt: "Audience portrait 1",
    className: "left-[36%] top-[10%] h-[5.5rem] w-[5.5rem] md:h-[7.8rem] md:w-[7.8rem]",
    message: "I received great service.",
    bubblePosition: "below",
  },
  {
    src: "/website/images/audience-avatars/person2.png",
    alt: "Audience portrait 2",
    className: "left-[18%] top-[24%] h-[4.4rem] w-[4.4rem] md:h-[6.5rem] md:w-[6.5rem]",
    message: "I found the products I wanted at an excellent price.",
    bubblePosition: "below",
  },
  {
    src: "/website/images/audience-avatars/person3.png",
    alt: "Audience portrait 3",
    className: "right-[18%] top-[20%] h-[4.4rem] w-[4.4rem] md:h-[6.5rem] md:w-[6.5rem]",
    message: "I feel seen.",
    bubblePosition: "below",
  },
  {
    src: "/website/images/audience-avatars/person4.png",
    alt: "Audience portrait 4",
    className: "left-[8%] top-[50%] h-[3.9rem] w-[3.9rem] md:h-[5.2rem] md:w-[5.2rem]",
    message: "This shop gets me!",
    bubblePosition: "above",
  },
  {
    src: "/website/images/audience-avatars/person5.png",
    alt: "Audience portrait 5",
    className: "right-[10%] top-[48%] h-[3.9rem] w-[3.9rem] md:h-[5.2rem] md:w-[5.2rem]",
    message: "Everything feels effortless here.",
    bubblePosition: "above",
  },
  {
    src: "/website/images/audience-avatars/person6.png",
    alt: "Audience portrait 6",
    className: "left-[20%] bottom-[16%] h-[4.4rem] w-[4.4rem] md:h-[6.5rem] md:w-[6.5rem]",
    message: "They always know what I need next.",
    bubblePosition: "above",
  },
  {
    src: "/website/images/audience-avatars/person7.png",
    alt: "Audience portrait 7",
    className: "right-[22%] bottom-[12%] h-[4.4rem] w-[4.4rem] md:h-[6.5rem] md:w-[6.5rem]",
    message: "I keep coming back because it just works.",
    bubblePosition: "above",
  },
  {
    src: "/website/images/audience-avatars/person8.png",
    alt: "Audience portrait 8",
    className: "left-[48%] bottom-[6%] h-[3.9rem] w-[3.9rem] md:h-[5.2rem] md:w-[5.2rem]",
    message: "This feels personal.",
    bubblePosition: "above",
  },
];

const pocketSignals = [
  {
    id: "whatsapp",
    tooltip: "Meeting you where you're at.",
    className: "left-[4%] top-[46%] h-16 w-16 -translate-y-1/2 rotate-[-8deg] md:h-24 md:w-24",
    mobileClass: "translate-y-4 rotate-[-12deg]",
    shellClass: "border border-white/10 bg-emerald-400/6 shadow-[0_0_45px_rgba(52,211,153,0.08)] backdrop-blur-[1px]",
    iconKind: "whatsapp",
    iconClass: "h-10 w-10 text-emerald-300/45 md:h-14 md:w-14",
  },
  {
    id: "telegram",
    tooltip: "Signals arrive in the flow of conversation.",
    className: "left-[29%] top-[28%] h-14 w-14 rotate-[11deg] md:h-20 md:w-20",
    mobileClass: "-translate-y-3 rotate-[14deg]",
    shellClass: "border border-white/10 bg-sky-300/6 shadow-[0_0_40px_rgba(125,211,252,0.08)] backdrop-blur-[1px]",
    iconKind: "telegram",
    iconClass: "h-9 w-9 text-sky-200/42 md:h-11 md:w-11",
  },
  {
    id: "push",
    tooltip: "The right nudge lands before the moment passes.",
    className: "left-[55%] top-[36%] h-14 w-14 -translate-y-1/2 rotate-[7deg] md:h-20 md:w-20",
    mobileClass: "translate-y-1 rotate-[8deg]",
    shellClass: "border border-white/10 bg-cyan-300/5 shadow-[0_0_40px_rgba(103,232,249,0.07)] backdrop-blur-[1px]",
    iconKind: "push",
    iconClass: "h-8 w-8 text-cyan-200/38 md:h-10 md:w-10",
  },
  {
    id: "mobile",
    tooltip: "Your store stays within reach.",
    className: "right-[4%] top-[46%] h-16 w-16 -translate-y-1/2 rotate-[-10deg] md:h-24 md:w-24",
    mobileClass: "translate-y-5 rotate-[-13deg]",
    shellClass: "border border-white/10 bg-white/5 shadow-[0_0_45px_rgba(255,255,255,0.05)] backdrop-blur-[1px]",
    iconKind: "mobile",
    iconClass: "h-9 w-9 text-white/28 md:h-12 md:w-12",
  },
] as const;

const testimonials = [
  {
    quote: "Storita lets us understand our store through a simple WhatsApp chat - quick answers, no digging through analytics.",
    name: "",
    title: "Trusted by",
    company: "DXRACER.CO.IL",
    href: "https://dxracer.co.il/",
  },
  {
    quote: "Storita's platform delivers an AI business consultant who lives in our data, understands our market and delivers crucial, valuable business insights.",
    name: "",
    title: "Trusted by",
    company: "IVFPharmacy.com",
    href: "https://www.ivfpharmacy.com/",
  },
  {
    quote: "Identifying non-selling products required digging through reports, so it rarely happened. Now we see them immediately.",
    name: "Sapir Borchov",
    title: "Trusted by",
    company: "Soltam",
    href: "https://soltam.co.il/",
  },
];

function SignalCardVisual({ cardId, isActive }: { cardId: string; isActive: boolean }) {
  const motionClass = isActive ? "scale-100 opacity-100" : "scale-90 opacity-70";

  if (cardId === "channels") {
    return (
      <div className={`relative h-40 w-40 transition-all duration-700 ${motionClass} md:h-48 md:w-48`}>
        <div className="signal-visual-halo absolute inset-[18%] rounded-full bg-amber-300/8 blur-2xl" />
        <div className="signal-visual-orbit-ring signal-visual-orbit-ring-outer absolute inset-[12%] rounded-full border border-white/10" />
        <div className="signal-visual-orbit-ring signal-visual-orbit-ring-inner absolute inset-[24%] rounded-full border border-white/8" />
        <div className="signal-visual-spokes absolute inset-0">
          <div className="signal-visual-spoke absolute left-1/2 top-[12%] h-[38%] w-px -translate-x-1/2 bg-gradient-to-b from-white/0 via-white/16 to-white/0" />
          <div className="signal-visual-spoke absolute left-[18%] top-1/2 h-px w-[30%] -translate-y-1/2 bg-gradient-to-r from-white/0 via-white/16 to-white/0" />
          <div className="signal-visual-spoke absolute right-[18%] top-1/2 h-px w-[30%] -translate-y-1/2 bg-gradient-to-l from-white/0 via-white/16 to-white/0" />
          <div className="signal-visual-spoke absolute left-1/2 bottom-[12%] h-[38%] w-px -translate-x-1/2 bg-gradient-to-t from-white/0 via-white/16 to-white/0" />
        </div>
        <div className="signal-visual-hub absolute left-1/2 top-1/2 z-10 h-10 w-10 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/18 bg-white/10" />
        <div className="signal-visual-orbit signal-visual-orbit-a absolute inset-0">
          <div className="signal-visual-node signal-visual-node-a absolute left-1/2 top-[10%] h-4.5 w-4.5 -translate-x-1/2 rounded-full bg-amber-300/90 shadow-[0_0_22px_rgba(252,211,77,0.5)]" />
        </div>
        <div className="signal-visual-orbit signal-visual-orbit-b absolute inset-0">
          <div className="signal-visual-node signal-visual-node-b absolute right-[10%] top-1/2 h-4.5 w-4.5 -translate-y-1/2 rounded-full bg-cyan-300/90 shadow-[0_0_22px_rgba(103,232,249,0.5)]" />
        </div>
        <div className="signal-visual-orbit signal-visual-orbit-c absolute inset-0">
          <div className="signal-visual-node signal-visual-node-c absolute left-1/2 bottom-[10%] h-4.5 w-4.5 -translate-x-1/2 rounded-full bg-rose-300/90 shadow-[0_0_22px_rgba(253,164,175,0.5)]" />
        </div>
        <div className="signal-visual-orbit signal-visual-orbit-d absolute inset-0">
          <div className="signal-visual-node signal-visual-node-d absolute left-[10%] top-1/2 h-4.5 w-4.5 -translate-y-1/2 rounded-full bg-emerald-300/90 shadow-[0_0_22px_rgba(110,231,183,0.5)]" />
        </div>
        <div className="signal-visual-link signal-visual-link-a absolute left-1/2 top-[18%] h-[32%] w-px -translate-x-1/2 bg-gradient-to-b from-white/0 via-white/24 to-white/0" />
        <div className="signal-visual-link signal-visual-link-b absolute right-[18%] top-1/2 h-px w-[28%] -translate-y-1/2 bg-gradient-to-l from-white/0 via-white/24 to-white/0" />
        <div className="signal-visual-link signal-visual-link-c absolute left-1/2 bottom-[18%] h-[28%] w-px -translate-x-1/2 bg-gradient-to-t from-white/0 via-white/24 to-white/0" />
        <div className="signal-visual-link signal-visual-link-d absolute left-[18%] top-1/2 h-px w-[28%] -translate-y-1/2 bg-gradient-to-r from-white/0 via-white/24 to-white/0" />
        <div className="signal-visual-pulse signal-visual-pulse-a absolute left-1/2 top-1/2 h-24 w-24 -translate-x-1/2 -translate-y-1/2 rounded-full border border-amber-200/10" />
        <div className="signal-visual-pulse signal-visual-pulse-b absolute left-1/2 top-1/2 h-32 w-32 -translate-x-1/2 -translate-y-1/2 rounded-full border border-cyan-200/8" />
      </div>
    );
  }

  if (cardId === "ai-discovery") {
    return (
      <div className={`relative h-40 w-40 transition-all duration-700 ${motionClass} md:h-48 md:w-48`}>
        <div className="signal-discovery-ring signal-discovery-ring-outer absolute inset-6 rounded-full border border-fuchsia-200/18" />
        <div className="signal-discovery-ring signal-discovery-ring-inner absolute inset-12 rounded-full border border-fuchsia-200/16" />
        <div className="signal-discovery-beam absolute left-1/2 top-1/2 h-14 w-1 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-b from-white via-fuchsia-200 to-transparent" />
        <div className="signal-discovery-tile signal-discovery-tile-a absolute left-[28%] top-[26%] h-5 w-5 rotate-12 rounded-[0.7rem] border border-white/16 bg-white/8" />
        <div className="signal-discovery-ping signal-discovery-ping-a absolute right-[24%] top-[34%] h-3 w-3 rounded-full bg-fuchsia-300/80" />
        <div className="signal-discovery-ping signal-discovery-ping-b absolute left-[24%] bottom-[24%] h-4 w-4 rounded-full bg-cyan-300/80" />
        <div className="signal-discovery-tile signal-discovery-tile-b absolute right-[28%] bottom-[26%] h-5 w-5 -rotate-12 rounded-[0.7rem] border border-white/16 bg-white/8" />
      </div>
    );
  }

  if (cardId === "conversations") {
    return (
      <div className={`relative h-40 w-40 transition-all duration-700 ${motionClass} md:h-48 md:w-48`}>
        <div className="signal-conversation-card signal-conversation-card-top absolute left-[16%] top-[24%] w-24 rounded-[1.2rem] border border-white/14 bg-white/8 px-4 py-3">
          <div className="flex gap-1.5">
            <span className="signal-conversation-dot signal-conversation-dot-a h-2 w-2 rounded-full bg-white/75" />
            <span className="signal-conversation-dot signal-conversation-dot-b h-2 w-2 rounded-full bg-white/55" />
            <span className="signal-conversation-dot signal-conversation-dot-c h-2 w-2 rounded-full bg-white/35" />
          </div>
        </div>
        <div className="signal-conversation-card signal-conversation-card-bottom absolute left-[26%] top-[50%] w-28 rounded-[1.2rem] border border-emerald-200/16 bg-emerald-300/10 px-4 py-3">
          <div className="signal-conversation-line signal-conversation-line-a h-2 w-12 rounded-full bg-emerald-100/70" />
          <div className="signal-conversation-line signal-conversation-line-b mt-2 h-2 w-16 rounded-full bg-emerald-100/45" />
        </div>
        <div className="signal-conversation-orb absolute right-[18%] bottom-[22%] h-8 w-8 rounded-full border border-white/14 bg-white/8" />
      </div>
    );
  }

  return (
    <div className={`relative h-40 w-40 transition-all duration-700 ${motionClass} md:h-48 md:w-48`}>
      <div className="signal-radar-ring signal-radar-ring-outer absolute inset-5 rounded-full border border-cyan-200/14" />
      <div className="signal-radar-ring signal-radar-ring-inner absolute inset-[30%] rounded-full border border-cyan-200/16" />
      <div className="signal-radar-sweep-line absolute left-1/2 top-1/2 h-[40%] w-px -translate-x-1/2 -translate-y-full origin-bottom rotate-[36deg] bg-gradient-to-t from-cyan-300/80 to-transparent" />
      <div className="signal-radar-blip absolute left-[50%] top-[18%] h-3.5 w-3.5 -translate-x-1/2 rounded-full bg-cyan-300/80 shadow-[0_0_18px_rgba(103,232,249,0.4)]" />
      <div className="signal-radar-node signal-radar-node-a absolute left-[18%] bottom-[26%] h-5 w-5 rounded-[0.8rem] border border-white/14 bg-white/8" />
      <div className="signal-radar-node signal-radar-node-b absolute right-[20%] bottom-[24%] h-5 w-5 rounded-[0.8rem] border border-white/14 bg-white/8" />
    </div>
  );
}

function WhatsAppAmbientIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 64 64" fill="none" aria-hidden="true" className={className}>
      <path
        d="M32 8C19.297 8 9 18.073 9 30.5C9 35.584 10.73 40.27 13.643 44.033L11.5 55.5L23.225 52.52C25.925 53.806 28.957 54.5 32 54.5C44.703 54.5 55 44.427 55 32C55 19.573 44.703 8 32 8Z"
        fill="currentColor"
      />
      <path
        d="M24.69 22.692C23.733 22.692 22.842 23.246 22.401 24.111C21.312 26.246 20.763 29.029 21.669 31.952C23.47 37.767 28.383 42.483 34.42 44.025C37.171 44.727 39.77 44.112 41.758 42.95C42.546 42.49 43.042 41.677 43.042 40.776V37.902C43.042 37.404 42.713 36.965 42.235 36.825L37.704 35.495C37.286 35.372 36.837 35.484 36.53 35.788L34.954 37.347C34.607 37.69 34.084 37.797 33.622 37.621C30.985 36.615 28.668 34.425 27.578 31.929C27.39 31.498 27.489 31.006 27.826 30.682L29.472 29.098C29.793 28.79 29.91 28.331 29.778 27.91L28.359 23.382C28.208 22.902 27.763 22.578 27.263 22.59L24.69 22.692Z"
        fill="#020617"
      />
    </svg>
  );
}

function TelegramAmbientIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 64 64" fill="none" aria-hidden="true" className={className}>
      <circle cx="32" cy="32" r="24" fill="currentColor" />
      <path
        d="M45.88 21.24L40.714 46.16C40.324 47.923 39.308 48.36 37.866 47.544L29.82 41.611L25.938 45.349C25.509 45.777 25.15 46.136 24.324 46.136L24.902 37.936L39.838 24.443C40.488 23.865 39.694 23.545 38.83 24.123L20.364 35.747L12.41 33.261C10.68 32.719 10.648 31.53 12.769 30.699L43.841 18.72C45.28 18.174 46.538 19.038 45.88 21.24Z"
        fill="#020617"
      />
    </svg>
  );
}

function PocketSignalIcon({ kind, className }: { kind: (typeof pocketSignals)[number]["iconKind"]; className?: string }) {
  if (kind === "whatsapp") {
    return <WhatsAppAmbientIcon className={className} />;
  }

  if (kind === "telegram") {
    return <TelegramAmbientIcon className={className} />;
  }

  if (kind === "push") {
    return <BellRing className={className} />;
  }

  return <Smartphone className={className} />;
}

export default function HomePage() {
  const [showContactForm, setShowContactForm] = useState(false);
  const [taglineIndex, setTaglineIndex] = useState(0);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [isPocketShowcaseVisible, setIsPocketShowcaseVisible] = useState(false);
  const [activePocketPhonePulse, setActivePocketPhonePulse] = useState<"left" | "right" | null>(null);
  const [activeSignalCard, setActiveSignalCard] = useState(0);
  const [audienceRadarSweepKey, setAudienceRadarSweepKey] = useState(0);
  const [activeAudienceBubble, setActiveAudienceBubble] = useState(0);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [testimonialFading, setTestimonialFading] = useState(false);
  const [showSampleReport, setShowSampleReport] = useState(false);
  const [showIntroMovie, setShowIntroMovie] = useState(false);
  const pocketShowcaseRef = useRef<HTMLElement | null>(null);
  const audienceRadarSectionRef = useRef<HTMLElement | null>(null);
  const audienceRadarHasPlayedRef = useRef(false);
  const visibleAudienceAvatars = isMobileViewport ? audienceAvatars.slice(0, 6) : audienceAvatars;

  useEffect(() => {
    setTaglineIndex(Math.floor(Math.random() * taglineVariants.length));
  }, []);

  useEffect(() => {
    const updateViewport = () => {
      setIsMobileViewport(window.innerWidth < 768);
    };

    updateViewport();
    window.addEventListener("resize", updateViewport);

    return () => window.removeEventListener("resize", updateViewport);
  }, []);

  // Auto-cycle testimonials every 10s with fade
  useEffect(() => {
    const interval = setInterval(() => {
      setTestimonialFading(true);
      setTimeout(() => {
        setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
        setTestimonialFading(false);
      }, 400);
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!pocketShowcaseRef.current || !isMobileViewport) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        setIsPocketShowcaseVisible(Boolean(entry?.isIntersecting));
      },
      { threshold: 0.35 }
    );

    observer.observe(pocketShowcaseRef.current);

    return () => observer.disconnect();
  }, [isMobileViewport]);

  useEffect(() => {
    if (!isMobileViewport || !isPocketShowcaseVisible) {
      setActivePocketPhonePulse(null);
      return;
    }

    let rightPulseTimeout = 0;
    let pulseResetTimeout = 0;
    let nextPulseTimeout = 0;

    const triggerPulse = () => {
      setActivePocketPhonePulse("left");

      rightPulseTimeout = window.setTimeout(() => {
        setActivePocketPhonePulse("right");
      }, 380);

      pulseResetTimeout = window.setTimeout(() => {
        setActivePocketPhonePulse(null);
      }, 860);

      nextPulseTimeout = window.setTimeout(triggerPulse, 6500 + Math.round(Math.random() * 3500));
    };

    triggerPulse();

    return () => {
      window.clearTimeout(rightPulseTimeout);
      window.clearTimeout(pulseResetTimeout);
      window.clearTimeout(nextPulseTimeout);
      setActivePocketPhonePulse(null);
    };
  }, [isMobileViewport, isPocketShowcaseVisible]);

  useEffect(() => {
    if (!audienceRadarSectionRef.current) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;

        if (!entry?.isIntersecting || audienceRadarHasPlayedRef.current) {
          return;
        }

        audienceRadarHasPlayedRef.current = true;
        setAudienceRadarSweepKey((current) => current + 1);
        observer.disconnect();
      },
      { threshold: 0.45 }
    );

    observer.observe(audienceRadarSectionRef.current);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setAudienceRadarSweepKey((current) => current + 1);
    }, 10000);

    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    let timeoutId = 0;

    if (visibleAudienceAvatars.length === 0) {
      return;
    }

    const scheduleBubble = () => {
      timeoutId = window.setTimeout(() => {
        setActiveAudienceBubble((current) => (current + 1) % visibleAudienceAvatars.length);
        scheduleBubble();
      }, 2600 + Math.round(Math.random() * 3000));
    };

    scheduleBubble();

    return () => window.clearTimeout(timeoutId);
  }, [visibleAudienceAvatars.length]);

  useEffect(() => {
    if (activeAudienceBubble < visibleAudienceAvatars.length) {
      return;
    }

    setActiveAudienceBubble(0);
  }, [activeAudienceBubble, visibleAudienceAvatars.length]);

  return (
    <>
    <div className="relative bg-slate-950">
      {/* Hero Section */}
      <section className="relative flex min-h-[72svh] items-center overflow-hidden pt-14 pb-6 md:min-h-[calc(76svh-4rem)] md:pt-18 md:pb-8">
        {/* Background effects from old landing page */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="hidden md:block">
            <GlitterParticles />
          </div>
          <div className="absolute inset-0 bg-[url('/grid.svg')] bg-center opacity-10 md:opacity-20 [mask-image:linear-gradient(180deg,white,rgba(255,255,255,0))]" />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full -z-10">
            <div className="absolute top-[-10%] left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-purple-600/20 blur-[120px] rounded-full opacity-50" />
          </div>
        </div>

        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="max-w-5xl mx-auto text-center">
            <ScrollReveal delay={100}>
              <h1 className="mb-8 md:mb-10">
                <span className={`block bg-gradient-to-b from-white to-slate-400 bg-clip-text text-transparent ${websiteHeroTitleText}`}>
                  {heroTitle}
                </span>
                <span className={`mt-2 block bg-[linear-gradient(90deg,#f9a8d4_0%,#ffffff_18%,#c4b5fd_38%,#67e8f9_66%,#fde68a_100%)] bg-clip-text text-transparent md:mt-3 ${websiteHeroTitleText}`}>
                  {heroSignature}
                </span>
              </h1>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <p className={`mx-auto mb-10 max-w-2xl text-slate-300 ${websiteBodyText}`}>
                {heroSubtext}
              </p>
            </ScrollReveal>
            
            <ScrollReveal delay={300}>
              <div className="mb-6 flex flex-col items-center justify-center gap-3">
                <WebsiteCtaLink href="/login" className="w-full sm:w-auto" fullWidth size="hero">
                  Start Now
                </WebsiteCtaLink>
                <div className="grid w-full max-w-[30rem] grid-cols-[1fr_auto_1fr] items-center gap-x-3 text-sm text-slate-500">
                  <span className="justify-self-end text-right leading-none">No credit card required</span>
                  <span className="grid h-4 w-4 place-items-center" aria-hidden="true">
                    <span className="block h-1.5 w-1.5 rounded-full bg-slate-500/70" />
                  </span>
                  <span className="justify-self-start text-left leading-none">30 days free trial</span>
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-slate-500 text-base font-medium mb-12">
                {taglineVariants[taglineIndex].map((item, i) => (
                  <span key={i}><span className="text-purple-400">{item.highlight}</span>{item.rest}.</span>
                ))}
              </div>
            </ScrollReveal>

          </div>
        </div>
      </section>

      <FeatureGallery />

      <IntegrationsSection />

      <section className="py-10 md:py-14 bg-slate-950">
        <div className="container mx-auto px-4 md:px-6">
          <ScrollReveal>
            <div className="mx-auto max-w-3xl text-center">
              <button
                type="button"
                onClick={() => setShowIntroMovie(true)}
                className="group inline-flex w-full items-center justify-center gap-3 rounded-full bg-slate-900/60 px-5 py-3.5 text-left text-slate-200 transition-all hover:bg-slate-900/90 md:w-auto"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-purple-500/15 text-purple-300 transition-colors group-hover:bg-purple-500/20 group-hover:text-purple-200">
                  <Play className="h-4 w-4 fill-current" />
                </span>
                <span className="flex flex-col items-start">
                  <span className="text-base md:text-lg font-medium text-white">Storita - The Age of Intelligence</span>
                  <span className="text-xs text-slate-500">Watch intro</span>
                </span>
              </button>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <FocusGallery />

      <BeforeAfterGallery />

      <section className="py-24 bg-slate-900/40">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid items-center gap-14 lg:grid-cols-[0.95fr_1.05fr]">
            <ScrollReveal className="order-2 lg:order-1">
              <div className="mx-auto max-w-[390px] lg:max-w-[420px]">
                <div className="relative overflow-hidden rounded-[2rem] border border-slate-800 bg-slate-950/70 p-3 shadow-2xl shadow-black/20">
                  <div className="absolute inset-x-0 top-0 z-10 h-20 bg-gradient-to-b from-slate-950/60 to-transparent pointer-events-none" />
                  <Image
                    src="/website/images/homepage/daily-report-mail.jpg"
                    alt="Storita daily revenue brief email example"
                    width={945}
                    height={2048}
                    className="h-[560px] md:h-[640px] w-full rounded-[1.5rem] border border-slate-800/80 object-cover object-top"
                  />
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={150} className="order-1 lg:order-2">
              <div className="max-w-2xl text-center lg:text-left">
                <p className={`mb-5 text-purple-300 ${websiteEyebrowText}`}>Daily Revenue Brief Example</p>
                <h2 className={`mb-5 text-white ${websiteSectionTitleText}`}>
                  Wake up to the <span className="bg-gradient-to-r from-purple-200 via-white to-cyan-200 bg-clip-text text-transparent drop-shadow-[0_0_18px_rgba(192,132,252,0.4)]">signal</span>.
                </h2>
                <p className={`mb-8 text-slate-300 ${websiteBodyText}`}>
                  One brief shows what changed, what matters, and where revenue is moving.
                </p>
                <button
                  type="button"
                  onClick={() => setShowSampleReport(true)}
                  className="inline-flex items-center justify-center rounded-xl border border-cyan-400/30 bg-cyan-400/10 px-6 py-4 font-semibold text-cyan-100 transition-colors hover:border-cyan-300/50 hover:bg-cyan-300/14 hover:text-white"
                >
                  View a sample daily report
                </button>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      <UndercoverAgentsSection />

      {/* AI Dynamic Chat Section */}
      <section className="py-24 bg-slate-950">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            <ScrollReveal>
              <div className="text-center mb-16">
                <p className={`mb-4 text-purple-300 ${websiteEyebrowText}`}>AI Dynamic Chat</p>
                <h2 className={`mx-auto max-w-4xl text-white ${websiteSectionTitleText}`}>
                  See the <span className="bg-gradient-to-r from-purple-200 via-fuchsia-300 to-cyan-200 bg-clip-text text-transparent drop-shadow-[0_0_18px_rgba(192,132,252,0.45)]">next move</span> instantly.
                </h2>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={200}>
              <div className="relative overflow-hidden rounded-[2rem] border border-slate-800/70 bg-slate-900/45 p-4 md:p-6 shadow-2xl shadow-black/25">
                <div className="absolute inset-x-[18%] top-[-12%] h-40 rounded-full bg-purple-500/16 blur-3xl" />
                <div className="absolute inset-x-[26%] bottom-[-20%] h-40 rounded-full bg-cyan-400/10 blur-3xl" />
                <div className="relative">
                  <AnalysisPlayback />
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Mobile Section */}
      <section ref={pocketShowcaseRef} className="overflow-visible py-24 bg-slate-900/50 md:overflow-hidden">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-12">
            <ScrollReveal>
              <div className="relative max-w-xl overflow-visible text-center lg:text-left">
                <h2 className={`mx-auto mb-5 max-w-[14ch] text-white lg:mx-0 ${websiteSectionTitleText}`}>
                  Your store, <span className="bg-gradient-to-r from-purple-200 via-white to-cyan-200 bg-clip-text text-transparent drop-shadow-[0_0_18px_rgba(192,132,252,0.4)]">in your pocket</span>.
                </h2>
                <p className={`mb-8 text-slate-300 ${websiteBodyText}`}>
                  Get the signals. Act before the moment passes.
                </p>
                <div className="hidden md:block">
                  <WebsiteCtaLink href="/login">Start Now</WebsiteCtaLink>
                </div>
                {isMobileViewport ? (
                  <div className="mt-4 flex w-full max-w-[20rem] items-center justify-center gap-3">
                    {pocketSignals.map((signal, index) => (
                      <div
                        key={signal.id}
                        className={`pocket-signal-float ${signal.mobileClass}`}
                        style={{
                          animationDuration: `${8.5 + index * 0.8}s`,
                          animationDelay: `${index * -1.2}s`,
                        }}
                      >
                        <div className={`flex h-15 w-15 items-center justify-center rounded-full ${signal.shellClass}`} aria-label={signal.tooltip}>
                          <PocketSignalIcon kind={signal.iconKind} className={signal.iconClass} />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="relative mt-10 h-32 w-full max-w-md overflow-visible">
                    {pocketSignals.map((signal, index) => (
                      <div
                        key={signal.id}
                        className={`absolute pocket-signal-float ${signal.className}`}
                        style={{
                          animationDuration: `${8.5 + index * 0.8}s`,
                          animationDelay: `${index * -1.2}s`,
                        }}
                      >
                        <button
                          type="button"
                          className="group relative block h-full w-full cursor-default rounded-full focus:outline-none"
                          aria-label={signal.tooltip}
                        >
                          <div className={`flex h-full w-full items-center justify-center rounded-full transition-transform duration-300 ease-out group-hover:scale-110 group-focus-visible:scale-110 ${signal.shellClass}`}>
                            <PocketSignalIcon kind={signal.iconKind} className={signal.iconClass} />
                          </div>
                          <div className="pointer-events-none absolute bottom-[calc(100%+0.75rem)] left-1/2 z-20 w-max max-w-[11rem] -translate-x-1/2 rounded-2xl border border-white/10 bg-slate-950/92 px-3 py-2 text-center text-xs leading-relaxed text-white/88 opacity-0 shadow-[0_18px_40px_rgba(2,6,23,0.45)] backdrop-blur-lg transition-all duration-300 group-hover:-translate-y-1 group-hover:opacity-100 group-focus-visible:-translate-y-1 group-focus-visible:opacity-100">
                            {signal.tooltip}
                            <span className="absolute left-1/2 top-full h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rotate-45 border-b border-r border-white/10 bg-slate-950/92" />
                          </div>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              {isMobileViewport ? (
                <div className="relative z-0 mx-auto mt-4 flex w-full max-w-[18.5rem] justify-center gap-2 overflow-visible">
                  <div className={`pocket-phone-card pocket-phone-card-left ${activePocketPhonePulse === "left" ? "pocket-phone-card-left-active" : ""} relative aspect-[9/19] w-[42%] max-w-[9rem] overflow-hidden rounded-[2rem] border-[3px] border-slate-800 bg-black shadow-2xl`}>
                    <Image 
                      src="/website/images/features/mobile_dashboard.webp" 
                      alt="Mobile Dashboard" 
                      fill
                      className="object-cover"
                    />
                  </div>

                  <div className={`pocket-phone-card pocket-phone-card-right ${activePocketPhonePulse === "right" ? "pocket-phone-card-right-active" : ""} relative aspect-[9/19] w-[42%] max-w-[9rem] overflow-hidden rounded-[2rem] border-[3px] border-slate-800 bg-black shadow-2xl`}>
                    <Image 
                      src="/website/mobile_agent.png" 
                      alt="Mobile AI Agent" 
                      fill
                      className="object-cover"
                    />
                  </div>
                </div>
              ) : (
                <div className="relative flex justify-center gap-4 lg:justify-end">
                  <div className="absolute inset-0 -z-10 rounded-full bg-purple-500/20 blur-[100px]" />
                  <div className="relative aspect-[9/19] w-[240px] overflow-hidden rounded-[2.5rem] border-4 border-slate-800 bg-slate-900 shadow-2xl transition-transform duration-500 -rotate-6 hover:rotate-0 md:w-[280px]">
                    <Image 
                      src="/website/images/features/mobile_dashboard.webp" 
                      alt="Mobile Dashboard" 
                      fill
                      className="object-cover"
                    />
                  </div>
                  <div className="relative aspect-[9/19] w-[240px] translate-y-12 overflow-hidden rounded-[2.5rem] border-4 border-slate-800 bg-slate-900 shadow-2xl transition-transform duration-500 rotate-6 hover:rotate-0 md:w-[280px]">
                    <Image 
                      src="/website/mobile_agent.png" 
                      alt="Mobile AI Agent" 
                      fill
                      className="object-cover"
                    />
                  </div>
                </div>
              )}
            </ScrollReveal>
          </div>
        </div>
      </section>

      <section ref={audienceRadarSectionRef} className="relative overflow-hidden border-t border-slate-900/50 bg-slate-950 py-24">
        <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_20%_18%,rgba(168,85,247,0.12),transparent_22%),radial-gradient(circle_at_80%_22%,rgba(34,211,238,0.1),transparent_20%),radial-gradient(circle_at_50%_82%,rgba(236,72,153,0.1),transparent_26%)]" />
        <div className="container relative z-10 mx-auto px-4 md:px-6">
          <ScrollReveal>
            <div className="grid gap-8 lg:grid-cols-[0.95fr_0.7fr] lg:items-end">
              <div className="text-center lg:text-left">
                <p className={`text-slate-500 ${websiteEyebrowText}`}>Every input, one foundation</p>
                <h2 className={`mx-auto mt-4 max-w-3xl text-white lg:mx-0 ${websiteSectionTitleText}`}>
                  One <span className="bg-[linear-gradient(90deg,#f9a8d4_0%,#ffffff_18%,#c4b5fd_38%,#67e8f9_66%,#fde68a_100%)] bg-clip-text text-transparent">clear view</span>.
                </h2>
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={120}>
            <div className="mt-14 flex flex-col gap-3 md:h-[34rem] md:flex-row">
              {signalFoundationCards.map((card, index) => {
                const isActive = index === activeSignalCard;

                return (
                  <button
                    key={card.id}
                    type="button"
                    onMouseEnter={() => {
                      if (!isMobileViewport) {
                        setActiveSignalCard(index);
                      }
                    }}
                    onFocus={() => setActiveSignalCard(index)}
                    onClick={() => setActiveSignalCard(index)}
                    className={`group relative overflow-hidden rounded-[2rem] border p-4 text-left shadow-[0_24px_80px_rgba(2,6,23,0.28)] transition-all duration-700 ease-out md:h-full md:p-6 ${card.cardClass} ${isActive ? "min-h-[20rem]" : "min-h-[5.75rem]"}`}
                    style={{ flex: isActive ? 3.2 : 1 }}
                    aria-pressed={isActive}
                  >
                    <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-[2rem]">
                      <div
                        className={`signal-card-ambient signal-card-ambient-primary ${card.glowClass}`}
                        style={{ ["--signal-delay" as string]: `${index * 0.45}s` } as CSSProperties}
                      />
                      <div
                        className={`signal-card-ambient signal-card-ambient-secondary ${card.glowClass}`}
                        style={{ ["--signal-delay" as string]: `${0.8 + index * 0.35}s` } as CSSProperties}
                      />
                      <div className="signal-card-grid" />
                      <div
                        className="signal-card-sheen"
                        style={{ ["--signal-delay" as string]: `${1.1 + index * 0.3}s` } as CSSProperties}
                      />
                    </div>
                    <div className={`absolute inset-x-0 top-0 h-24 blur-3xl transition-opacity duration-700 ${card.glowClass} ${isActive ? "opacity-100" : "opacity-40"}`} />
                    <div className="relative h-full">
                      <div className="relative z-10 text-center md:hidden">
                        <h3 className="mx-auto max-w-[12ch] text-lg font-semibold leading-tight text-white">
                          {card.eyebrow}
                        </h3>

                        {isActive ? (
                          <div className="mt-5">
                            <div className="flex justify-center">
                              <SignalCardVisual cardId={card.id} isActive={isActive} />
                            </div>
                            <h4 className="mx-auto mt-5 max-w-[15ch] text-2xl font-semibold leading-tight text-white">
                              {card.title}
                            </h4>
                            <p className="mx-auto mt-3 max-w-[30ch] text-sm leading-relaxed text-slate-300">
                              {card.description}
                            </p>
                            <div className="mt-5 flex flex-wrap justify-center gap-2">
                              {card.chips.map((chip) => (
                                <span key={chip} className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.18em] text-white/80">
                                  {chip}
                                </span>
                              ))}
                            </div>
                          </div>
                        ) : null}
                      </div>

                      <div className={`absolute inset-0 hidden items-center justify-center transition-all duration-500 md:flex ${isActive ? "opacity-0 md:pointer-events-none md:scale-90" : "opacity-100 scale-100"}`}>
                        <div className="flex max-w-[10rem] flex-col items-center text-center">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-white/58 md:text-xs">{card.eyebrow}</p>
                          <h3 className="mt-4 text-xl font-semibold leading-tight text-white md:text-[1.7rem]">
                            {card.title}
                          </h3>
                        </div>
                      </div>

                      <div className={`absolute inset-0 hidden items-center justify-center transition-all duration-700 md:flex ${isActive ? "opacity-100 scale-100" : "opacity-0 scale-90"}`}>
                        <div className="flex w-full max-w-md flex-col items-center text-center">
                          <SignalCardVisual cardId={card.id} isActive={isActive} />
                          <p className="mt-2 text-xs font-semibold uppercase tracking-[0.28em] text-white/58">{card.eyebrow}</p>
                          <h3 className="mt-4 max-w-xs text-3xl font-semibold leading-tight text-white md:text-[2.2rem]">
                            {card.title}
                          </h3>
                          <p className="mt-4 max-w-sm text-base leading-relaxed text-slate-300 md:text-lg">
                            {card.description}
                          </p>
                          <div className="mt-8 flex flex-wrap justify-center gap-2">
                            {card.chips.map((chip) => (
                              <span key={chip} className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium uppercase tracking-[0.18em] text-white/80">
                                {chip}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </ScrollReveal>

        </div>
      </section>

      <section
        ref={audienceRadarSectionRef}
        className="relative overflow-hidden border-t border-slate-900/50 bg-slate-950 py-24"
      >
        <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_20%_18%,rgba(168,85,247,0.12),transparent_22%),radial-gradient(circle_at_80%_22%,rgba(34,211,238,0.1),transparent_20%),radial-gradient(circle_at_50%_82%,rgba(236,72,153,0.1),transparent_26%)]" />
        <div className="container relative z-10 mx-auto px-4 md:px-6">
          <ScrollReveal>
            <div className="grid gap-8 lg:grid-cols-[0.95fr_0.7fr] lg:items-end">
              <div className="text-center lg:text-left">
                <p className={`text-slate-500 ${websiteEyebrowText}`}>Human attention, made visible</p>
                <h2 className={`mx-auto mt-4 max-w-3xl text-white lg:mx-0 ${websiteSectionTitleText}`}>
                  See where <span className="bg-[linear-gradient(90deg,#f9a8d4_0%,#ffffff_18%,#c4b5fd_38%,#67e8f9_66%,#fde68a_100%)] bg-clip-text text-transparent">attention</span> is forming.
                </h2>
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={120}>
            <div
              className="relative mx-auto mt-14 h-[34rem] max-w-[68rem] overflow-hidden rounded-[2.6rem] border border-white/10 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.08),transparent_34%),linear-gradient(180deg,rgba(15,23,42,0.96),rgba(2,6,23,0.98))] px-4 py-6 shadow-[0_30px_120px_rgba(2,6,23,0.5)] md:h-[40rem] md:px-8 md:py-8"
              onClick={() => setAudienceRadarSweepKey((current) => current + 1)}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  setAudienceRadarSweepKey((current) => current + 1);
                }
              }}
              aria-label="Replay audience radar sweep"
            >
              <div className="absolute left-1/2 top-1/2 h-[22rem] w-[22rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(244,114,182,0.12),transparent_58%)] blur-3xl md:h-[28rem] md:w-[28rem]" />
              <div className="absolute left-1/2 top-1/2 h-[16rem] w-[16rem] -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/10 md:h-[20rem] md:w-[20rem]" />
              <div className="absolute left-1/2 top-1/2 h-[11rem] w-[11rem] -translate-x-1/2 -translate-y-1/2 rounded-full border border-dashed border-white/12 md:h-[14rem] md:w-[14rem]" />

              {audienceRadarSweepKey > 0 ? (
                <div
                  key={audienceRadarSweepKey}
                  className="audience-radar-sweep pointer-events-none absolute left-1/2 top-1/2 z-10 h-[16rem] w-[16rem] -translate-x-1/2 -translate-y-1/2 rounded-full md:h-[20rem] md:w-[20rem]"
                />
              ) : null}

              {visibleAudienceAvatars.map((avatar, index) => (
                <div
                  key={`${audienceRadarSweepKey}-${avatar.src}`}
                  className={`absolute audience-avatar-drift ${avatar.className}`}
                  style={{
                    animationDuration: `${9 + (index % 4) * 1.2}s`,
                    animationDelay: `${index * -0.9}s`,
                    ["--float-x" as string]: `${index % 2 === 0 ? 8 : -10}px`,
                    ["--float-y" as string]: `${index % 3 === 0 ? -12 : 10}px`,
                    zIndex: activeAudienceBubble === index ? 70 : 20 + index,
                  } as CSSProperties}
                >
                  <div
                    className={`audience-avatar-quote z-40 ${avatar.bubblePosition === "below" ? "audience-avatar-quote-below" : "audience-avatar-quote-above"} ${activeAudienceBubble === index ? "audience-avatar-quote-active" : ""}`}
                  >
                    <span>{avatar.message}</span>
                    <span className={`audience-avatar-quote-tail ${avatar.bubblePosition === "below" ? "audience-avatar-quote-tail-top" : "audience-avatar-quote-tail-bottom"}`} />
                  </div>
                  <div
                    className={audienceRadarSweepKey > 0 ? "audience-avatar-shell z-20 audience-avatar-react" : "audience-avatar-shell z-20"}
                    style={{ ["--radar-delay" as string]: `${0.18 + index * 0.15}s` } as CSSProperties}
                  >
                    <div className={`relative h-full w-full overflow-hidden rounded-full border border-white/14 bg-white/5 shadow-[0_18px_40px_rgba(2,6,23,0.28)] ${index % 2 === 0 ? "rotate-[-4deg]" : "rotate-[5deg]"}`}>
                      <Image src={avatar.src} alt={avatar.alt} fill className="object-cover" />
                    </div>
                  </div>
                </div>
              ))}

              <div className="absolute left-[28%] top-[32%] h-px w-[18%] rotate-[12deg] bg-gradient-to-r from-white/0 via-white/16 to-white/0" />
              <div className="absolute right-[26%] top-[34%] h-px w-[17%] rotate-[-14deg] bg-gradient-to-r from-white/0 via-white/16 to-white/0" />
              <div className="absolute left-[22%] bottom-[28%] h-px w-[16%] rotate-[-18deg] bg-gradient-to-r from-white/0 via-white/14 to-white/0" />
              <div className="absolute right-[24%] bottom-[26%] h-px w-[16%] rotate-[20deg] bg-gradient-to-r from-white/0 via-white/14 to-white/0" />

              <div className="absolute left-1/2 top-1/2 z-20 -translate-x-1/2 -translate-y-1/2 md:hidden">
                <div className="flex flex-col items-center gap-2 text-white">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full border border-white/12 bg-slate-950/72 backdrop-blur-md">
                    <Image src="/logo.png" alt="Storita logo" width={22} height={22} className="h-5.5 w-5.5 object-contain" />
                  </div>
                  <span className="radar-loading-dots text-xl font-semibold leading-none" aria-label="Gathering signals">
                    <span>.</span>
                    <span>.</span>
                    <span>.</span>
                  </span>
                </div>
              </div>

              <div className="absolute left-1/2 top-1/2 z-20 hidden w-[15rem] -translate-x-1/2 -translate-y-1/2 md:block md:w-[19rem]">
                <div className="rounded-[2rem] border border-white/12 bg-slate-950/82 p-5 text-center shadow-[0_24px_90px_rgba(2,6,23,0.45)] backdrop-blur-xl md:p-7">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-white/10 bg-white/5 md:h-16 md:w-16">
                    <Image src="/logo.png" alt="Storita logo" width={36} height={36} className="h-9 w-9 object-contain md:h-10 md:w-10" />
                  </div>
                  <div className="mt-4 inline-flex items-center justify-center gap-1 text-lg font-semibold text-white md:text-xl">
                    <span>Gathering signals</span>
                    <span className="radar-loading-dots" aria-hidden="true">
                      <span>.</span>
                      <span>.</span>
                      <span>.</span>
                    </span>
                  </div>
                </div>
              </div>

              <div className="absolute bottom-[3.5%] left-[2.5%] z-50 rounded-full border border-white/10 bg-slate-950/88 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-white/72 backdrop-blur-sm md:bottom-[6%] md:left-[4%] md:px-4 md:text-sm md:tracking-[0.22em]">
                Demand forming
              </div>
              <div className="absolute right-[2.5%] top-[3.5%] z-50 rounded-full border border-white/10 bg-slate-950/88 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-white/72 backdrop-blur-sm md:right-[4%] md:top-[6%] md:px-4 md:text-sm md:tracking-[0.22em]">
                High intent
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-24 bg-slate-950 border-t border-slate-900/50 relative overflow-hidden">
        <div className="absolute inset-0 bg-purple-900/5 pointer-events-none" />
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <ScrollReveal>
            <div className="max-w-3xl mx-auto">
              <div className="relative">
                {/* Large decorative quote mark */}
                <svg
                  className="absolute -top-4 -left-2 w-12 h-12 text-purple-500/30 select-none"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M14.017 21L14.017 18C14.017 16.8954 13.1216 16 12.017 16H9C9.55228 16 10 15.5523 10 15V9C10 8.44772 9.55228 8 9 8H5C4.44772 8 4 8.44772 4 9V15C4 18.3137 6.68629 21 10 21H14.017ZM21.017 21L21.017 18C21.017 16.8954 20.1216 16 19.017 16H16C16.5523 16 17 15.5523 17 15V9C17 8.44772 16.5523 8 16 8H12C11.4477 8 11 8.44772 11 9V15C11 18.3137 13.6863 21 17 21H21.017Z" />
                </svg>

                {/* Testimonial body */}
                <div
                  className={`transition-opacity duration-400 ${
                    testimonialFading ? "opacity-0" : "opacity-100"
                  }`}
                >
                  <p className="text-xl md:text-2xl font-medium text-slate-200 leading-relaxed mb-8 pl-10">
                    &ldquo;{testimonials[activeTestimonial].quote}&rdquo;
                  </p>

                  <div className="flex items-center justify-end gap-3">
                    <div className="text-right">
                      {testimonials[activeTestimonial].name && (
                        <p className="text-white font-bold text-base">
                          {testimonials[activeTestimonial].name}
                        </p>
                      )}
                      <p className="text-slate-400 text-sm uppercase tracking-wide font-medium">
                        {testimonials[activeTestimonial].title}&nbsp;
                        {testimonials[activeTestimonial].href ? (
                          <a
                            href={testimonials[activeTestimonial].href!}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-white hover:text-purple-300 transition-colors font-bold"
                          >
                            {testimonials[activeTestimonial].company}
                          </a>
                        ) : (
                          <span className="text-white font-bold">{testimonials[activeTestimonial].company}</span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Dot indicators — only shown when there are multiple testimonials */}
                {testimonials.length > 1 && (
                  <div className="flex justify-center gap-2 mt-10">
                    {testimonials.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => {
                          setTestimonialFading(true);
                          setTimeout(() => {
                            setActiveTestimonial(i);
                            setTestimonialFading(false);
                          }, 400);
                        }}
                        className={`w-2 h-2 rounded-full transition-all duration-300 ${
                          i === activeTestimonial
                            ? "bg-purple-500 w-6"
                            : "bg-slate-600 hover:bg-slate-500"
                        }`}
                        aria-label={`Testimonial ${i + 1}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-purple-600/5 -z-10" />
        <div className="container mx-auto px-4 md:px-6">
          <ScrollReveal>
            <h2 className="mx-auto mb-6 max-w-5xl leading-[1.04] tracking-tight">
              <span className={`block bg-gradient-to-b from-white to-slate-400 bg-clip-text text-transparent ${websiteSectionTitleText}`}>
                Ready to lead the market with
              </span>
              <span className={`block bg-[linear-gradient(90deg,#f9a8d4_0%,#ffffff_18%,#c4b5fd_38%,#67e8f9_66%,#fde68a_100%)] bg-clip-text text-transparent ${websiteSectionTitleText}`}>
                AI for modern eCommerce?
              </span>
            </h2>
          </ScrollReveal>
          <ScrollReveal delay={200}>
            <div className="flex flex-col items-center justify-center gap-3 mt-10">
              <WebsiteCtaLink href="/login" className="w-full sm:w-auto" fullWidth>
                Start Now
              </WebsiteCtaLink>
              <div className="grid w-full max-w-[30rem] grid-cols-[1fr_auto_1fr] items-center gap-x-3 text-sm text-slate-500">
                <span className="justify-self-end text-right leading-none">No credit card required</span>
                <span className="grid h-4 w-4 place-items-center" aria-hidden="true">
                  <span className="block h-1.5 w-1.5 rounded-full bg-slate-500/70" />
                </span>
                <span className="justify-self-start text-left leading-none">30 days free trial</span>
              </div>
            </div>
          </ScrollReveal>
          
        </div>
      </section>

      {showIntroMovie && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-3 md:p-6">
          <button
            type="button"
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={() => setShowIntroMovie(false)}
            aria-label="Close Storita intro"
          />
          <div className="relative z-10 w-full max-w-7xl">
            <button
              type="button"
              onClick={() => setShowIntroMovie(false)}
              className="absolute right-4 top-4 z-20 rounded-full border border-slate-700 bg-slate-900/90 p-2 text-slate-300 transition-colors hover:text-white"
              aria-label="Close Storita intro"
            >
              <X className="h-4 w-4" />
            </button>
            <StarWarsIntro onShowDemo={() => setShowContactForm(true)} />
          </div>
        </div>
      )}

      {showSampleReport && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 bg-black/75 backdrop-blur-sm"
            onClick={() => setShowSampleReport(false)}
            aria-label="Close sample report preview"
          />
          <div className="relative z-10 flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-[2rem] border border-slate-800 bg-slate-950 p-4 md:p-6 shadow-2xl shadow-black/40">
            <button
              type="button"
              onClick={() => setShowSampleReport(false)}
              className="absolute right-4 top-4 rounded-full border border-slate-700 bg-slate-900 p-2 text-slate-300 transition-colors hover:text-white"
              aria-label="Close sample report preview"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="mb-5 pr-12">
              <h2 className="text-2xl md:text-3xl font-bold text-white">Sample Revenue Report Experience</h2>
              <p className="mt-2 text-slate-400">
                Toggle between daily, monthly, and yearly views to see how one lively report can highlight what matters and what to do next.
              </p>
            </div>
            <div className="min-h-0 flex-1 overflow-auto rounded-[1.5rem] border border-slate-800 bg-slate-950">
              <SampleReportPreview initialRange="daily" />
            </div>
          </div>
        </div>
      )}

    </div>
      <style jsx global>{`
        .audience-radar-sweep {
          opacity: 0;
          animation: audienceRadarSweep 2.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        .audience-radar-sweep::before,
        .audience-radar-sweep::after {
          content: "";
          position: absolute;
          inset: -10%;
          border-radius: 9999px;
        }

        .audience-radar-sweep::before {
          border: 1px solid rgba(255, 255, 255, 0.16);
          box-shadow:
            0 0 0 1px rgba(125, 211, 252, 0.08),
            0 0 42px rgba(34, 211, 238, 0.16),
            inset 0 0 36px rgba(244, 114, 182, 0.1);
          animation: audienceRadarRipple 2.8s ease-out forwards;
        }

        .audience-radar-sweep::after {
          inset: -18%;
          background:
            radial-gradient(circle, rgba(255, 255, 255, 0.12) 0%, rgba(125, 211, 252, 0.08) 30%, rgba(2, 6, 23, 0) 60%),
            conic-gradient(
              from 0deg,
              rgba(125, 211, 252, 0) 0deg,
              rgba(125, 211, 252, 0.04) 292deg,
              rgba(125, 211, 252, 0.12) 320deg,
              rgba(244, 114, 182, 0.42) 338deg,
              rgba(255, 255, 255, 0.82) 350deg,
              rgba(125, 211, 252, 0) 360deg
            );
          -webkit-mask: radial-gradient(circle, transparent 0 46%, rgba(0, 0, 0, 0.96) 54%, transparent 70%);
          mask: radial-gradient(circle, transparent 0 46%, rgba(0, 0, 0, 0.96) 54%, transparent 70%);
        }

        .audience-avatar-drift {
          will-change: transform;
          animation-name: audienceAvatarDrift;
          animation-timing-function: ease-in-out;
          animation-iteration-count: infinite;
          animation-direction: alternate;
        }

        .audience-avatar-shell {
          height: 100%;
          width: 100%;
          transform-origin: center;
          will-change: transform, filter;
        }

        .audience-avatar-quote {
          pointer-events: none;
          position: absolute;
          left: 50%;
          z-index: 30;
          width: max-content;
          max-width: 14rem;
          border: 1px solid rgba(255, 255, 255, 0.12);
          border-radius: 1.25rem;
          background: rgba(15, 23, 42, 0.92);
          padding: 0.75rem 1rem;
          color: rgba(255, 255, 255, 0.94);
          font-size: 0.875rem;
          font-weight: 500;
          line-height: 1.5;
          text-align: center;
          box-shadow: 0 18px 40px rgba(2, 6, 23, 0.42);
          backdrop-filter: blur(16px);
          opacity: 0;
          transition: opacity 320ms ease, transform 320ms ease;
        }

        .audience-avatar-quote-above {
          bottom: calc(100% + 0.85rem);
          transform: translateX(-50%) translateY(0.5rem) scale(0.96);
        }

        .audience-avatar-quote-below {
          top: calc(100% + 0.85rem);
          transform: translateX(-50%) translateY(-0.5rem) scale(0.96);
        }

        .audience-avatar-quote-active {
          opacity: 1;
          transform: translateX(-50%) translateY(0) scale(1);
        }

        .audience-avatar-quote-tail {
          position: absolute;
          left: 50%;
          height: 0.75rem;
          width: 0.75rem;
          margin-left: -0.375rem;
          rotate: 45deg;
          background: rgba(15, 23, 42, 0.92);
        }

        .audience-avatar-quote-tail-bottom {
          top: 100%;
          margin-top: -0.38rem;
          border-right: 1px solid rgba(255, 255, 255, 0.12);
          border-bottom: 1px solid rgba(255, 255, 255, 0.12);
        }

        .audience-avatar-quote-tail-top {
          bottom: 100%;
          margin-bottom: -0.38rem;
          border-left: 1px solid rgba(255, 255, 255, 0.12);
          border-top: 1px solid rgba(255, 255, 255, 0.12);
        }

        .audience-avatar-react {
          animation: audienceAvatarRadar 2.8s cubic-bezier(0.22, 1, 0.36, 1) both;
          animation-delay: var(--radar-delay, 0s);
        }

        .pocket-signal-float {
          z-index: 20;
          will-change: transform;
          animation: pocketSignalFloat 8s ease-in-out infinite alternate;
        }

        .pocket-phone-card {
          position: relative;
          z-index: 10;
          will-change: transform;
        }

        @media (max-width: 767px) {
          .pocket-phone-card-left {
            transform: rotate(-6deg);
          }

          .pocket-phone-card-right {
            transform: translateY(1.5rem) rotate(6deg);
          }

          .pocket-phone-card-left-active {
            animation: pocketPhoneLeftNudge 1100ms cubic-bezier(0.16, 1, 0.3, 1) 1;
          }

          .pocket-phone-card-right-active {
            animation: pocketPhoneRightNudge 1100ms cubic-bezier(0.16, 1, 0.3, 1) 1;
          }
        }

        .signal-card-ambient {
          position: absolute;
          border-radius: 9999px;
          filter: blur(48px);
          opacity: 0.22;
          will-change: transform, opacity;
          animation: signalCardPulse 8.5s ease-in-out infinite alternate;
          animation-delay: var(--signal-delay, 0s);
        }

        .signal-card-ambient-primary {
          left: -6%;
          top: -8%;
          height: 9rem;
          width: 11rem;
        }

        .signal-card-ambient-secondary {
          right: -10%;
          bottom: -14%;
          height: 10rem;
          width: 12rem;
          opacity: 0.16;
          animation-name: signalCardDrift;
        }

        .signal-card-grid {
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px);
          background-size: 1.15rem 1.15rem;
          mask-image: linear-gradient(180deg, rgba(0,0,0,0.55), transparent 78%);
          opacity: 0.22;
          animation: signalCardGridDrift 16s linear infinite;
        }

        .signal-card-sheen {
          position: absolute;
          inset: -35% auto -35% -10%;
          width: 42%;
          background: linear-gradient(90deg, rgba(255,255,255,0), rgba(255,255,255,0.085), rgba(255,255,255,0));
          transform: rotate(14deg) translateX(-150%);
          opacity: 0.5;
          animation: signalCardSheen 10s ease-in-out infinite;
          animation-delay: var(--signal-delay, 0s);
        }

        .signal-visual-hub {
          animation: signalHubPulse 3.8s ease-in-out infinite;
        }

        .signal-visual-halo {
          animation: signalHaloBreath 4.6s ease-in-out infinite;
        }

        .signal-visual-orbit-ring {
          animation: signalOrbitRingDrift 12s linear infinite;
        }

        .signal-visual-orbit-ring-inner {
          animation-duration: 9s;
          animation-direction: reverse;
        }

        .signal-visual-spokes {
          transform-origin: center;
          animation: signalSpokesRotate 16s linear infinite;
        }

        .signal-visual-orbit {
          transform-origin: center;
          animation: signalNodeOrbit 9.5s linear infinite;
        }

        .signal-visual-orbit-b {
          animation-duration: 11s;
          animation-direction: reverse;
        }

        .signal-visual-orbit-c {
          animation-duration: 12.5s;
        }

        .signal-visual-orbit-d {
          animation-duration: 10.5s;
          animation-direction: reverse;
        }

        .signal-visual-node {
          animation: signalNodePulse 3.1s ease-in-out infinite;
        }

        .signal-visual-node-b {
          animation-delay: 0.45s;
        }

        .signal-visual-node-c {
          animation-delay: 0.95s;
        }

        .signal-visual-node-d {
          animation-delay: 1.35s;
        }

        .signal-visual-link {
          transform-origin: left center;
          animation: signalLinkPulse 2.8s ease-in-out infinite;
        }

        .signal-visual-link-b {
          animation-delay: 0.35s;
        }

        .signal-visual-link-c {
          animation-delay: 0.8s;
        }

        .signal-visual-link-d {
          animation-delay: 1.15s;
        }

        .signal-visual-pulse {
          animation: signalSystemPulse 3.4s ease-out infinite;
        }

        .signal-visual-pulse-b {
          animation-delay: 1.15s;
        }

        .signal-discovery-ring {
          animation: signalDiscoveryRing 6.5s ease-in-out infinite;
        }

        .signal-discovery-ring-inner {
          animation-delay: 0.75s;
        }

        .signal-discovery-beam {
          transform-origin: center bottom;
          animation: signalDiscoveryBeam 4.4s ease-in-out infinite;
        }

        .signal-discovery-ping {
          animation: signalDiscoveryPing 2.8s ease-in-out infinite;
        }

        .signal-discovery-ping-b {
          animation-delay: 1.2s;
        }

        .signal-discovery-tile {
          animation: signalDiscoveryTile 4.8s ease-in-out infinite;
        }

        .signal-discovery-tile-b {
          animation-delay: 1s;
        }

        .signal-conversation-card {
          animation: signalConversationFloat 4.6s ease-in-out infinite;
        }

        .signal-conversation-card-bottom {
          animation-delay: 0.9s;
        }

        .signal-conversation-dot {
          animation: signalConversationTyping 1.4s ease-in-out infinite;
        }

        .signal-conversation-dot-b {
          animation-delay: 0.2s;
        }

        .signal-conversation-dot-c {
          animation-delay: 0.4s;
        }

        .signal-conversation-line {
          transform-origin: left center;
          animation: signalConversationLine 3.2s ease-in-out infinite;
        }

        .signal-conversation-line-b {
          animation-delay: 0.45s;
        }

        .signal-conversation-orb {
          animation: signalConversationOrb 3.8s ease-in-out infinite;
        }

        .signal-radar-ring {
          animation: signalRadarRing 5.8s ease-in-out infinite;
        }

        .signal-radar-ring-inner {
          animation-delay: 0.8s;
        }

        .signal-radar-sweep-line {
          animation: signalRadarSweepLine 4.6s cubic-bezier(0.22, 1, 0.36, 1) infinite;
        }

        .signal-radar-blip {
          animation: signalRadarBlip 2.4s ease-in-out infinite;
        }

        .signal-radar-node {
          animation: signalRadarNode 4.2s ease-in-out infinite;
        }

        .signal-radar-node-b {
          animation-delay: 1.2s;
        }

        .radar-loading-dots {
          display: inline-flex;
          width: 1.4rem;
          justify-content: flex-start;
        }

        .radar-loading-dots span {
          animation: radarDots 1.3s ease-in-out infinite;
          opacity: 0.2;
        }

        .radar-loading-dots span:nth-child(2) {
          animation-delay: 0.18s;
        }

        .radar-loading-dots span:nth-child(3) {
          animation-delay: 0.36s;
        }

        @keyframes audienceRadarSweep {
          0% {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.78) rotate(-18deg);
          }
          12% {
            opacity: 0.95;
          }
          100% {
            opacity: 0;
            transform: translate(-50%, -50%) scale(1.38) rotate(342deg);
          }
        }

        @keyframes audienceRadarRipple {
          0% {
            opacity: 0.18;
            transform: scale(0.82);
          }
          45% {
            opacity: 0.28;
          }
          100% {
            opacity: 0;
            transform: scale(1.18);
          }
        }

        @keyframes audienceAvatarDrift {
          0% {
            transform: translate3d(calc(var(--float-x, 0px) * -0.5), calc(var(--float-y, 0px) * -0.45), 0);
          }
          100% {
            transform: translate3d(var(--float-x, 0px), var(--float-y, 0px), 0);
          }
        }

        @keyframes audienceAvatarRadar {
          0%,
          54%,
          100% {
            transform: scale(1);
            filter: saturate(0.96) brightness(1);
          }
          66% {
            transform: scale(1.14);
            filter: saturate(1.08) brightness(1.06);
          }
          78% {
            transform: scale(1.06);
            filter: saturate(1.02) brightness(1.02);
          }
        }

        @keyframes pocketSignalFloat {
          0% {
            transform: translate3d(0, -4px, 0);
          }
          100% {
            transform: translate3d(0, 6px, 0);
          }
        }

        @keyframes pocketPhoneLeftNudge {
          0% {
            transform: rotate(-6deg);
          }

          30% {
            transform: translate3d(2px, -5px, 0) rotate(-4deg);
          }

          62% {
            transform: translate3d(-1px, 1px, 0) rotate(-6.4deg);
          }

          100% {
            transform: rotate(-6deg);
          }
        }

        @keyframes pocketPhoneRightNudge {
          0% {
            transform: translateY(1.5rem) rotate(6deg);
          }

          30% {
            transform: translate3d(-2px, calc(1.5rem - 5px), 0) rotate(4deg);
          }

          62% {
            transform: translate3d(1px, calc(1.5rem + 1px), 0) rotate(6.4deg);
          }

          100% {
            transform: translateY(1.5rem) rotate(6deg);
          }
        }

        @keyframes signalCardPulse {
          0% {
            transform: translate3d(-4%, -6%, 0) scale(0.92);
            opacity: 0.16;
          }
          100% {
            transform: translate3d(8%, 10%, 0) scale(1.14);
            opacity: 0.3;
          }
        }

        @keyframes signalCardDrift {
          0% {
            transform: translate3d(8%, 4%, 0) scale(0.94);
          }
          100% {
            transform: translate3d(-10%, -12%, 0) scale(1.12);
          }
        }

        @keyframes signalCardGridDrift {
          0% {
            transform: translate3d(0, 0, 0);
          }
          100% {
            transform: translate3d(-1.15rem, -1.15rem, 0);
          }
        }

        @keyframes signalCardSheen {
          0%,
          18% {
            transform: rotate(14deg) translateX(-150%);
            opacity: 0;
          }
          35% {
            opacity: 0.55;
          }
          52%,
          100% {
            transform: rotate(14deg) translateX(420%);
            opacity: 0;
          }
        }

        @keyframes signalHubPulse {
          0%,
          100% {
            transform: translate(-50%, -50%) scale(1);
            box-shadow: 0 0 0 0 rgba(255,255,255,0);
          }
          50% {
            transform: translate(-50%, -50%) scale(1.08);
            box-shadow: 0 0 0 12px rgba(255,255,255,0.03);
          }
        }

        @keyframes signalHaloBreath {
          0%,
          100% {
            transform: scale(0.92);
            opacity: 0.28;
          }
          50% {
            transform: scale(1.1);
            opacity: 0.5;
          }
        }

        @keyframes signalOrbitRingDrift {
          0% {
            transform: rotate(0deg) scale(0.98);
            opacity: 0.38;
          }
          50% {
            transform: rotate(180deg) scale(1.02);
            opacity: 0.56;
          }
          100% {
            transform: rotate(360deg) scale(0.98);
            opacity: 0.38;
          }
        }

        @keyframes signalSpokesRotate {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        @keyframes signalNodeOrbit {
          0% {
            transform: rotate(0deg) scale(0.96);
          }
          50% {
            transform: rotate(180deg) scale(1.04);
          }
          100% {
            transform: rotate(360deg) scale(0.96);
          }
        }

        @keyframes signalNodePulse {
          0%,
          100% {
            transform: scale(1);
            opacity: 0.78;
          }
          50% {
            transform: scale(1.18);
            opacity: 1;
          }
        }

        @keyframes signalLinkPulse {
          0%,
          100% {
            opacity: 0.18;
            transform: scale(0.88);
          }
          50% {
            opacity: 0.72;
            transform: scale(1.08);
          }
        }

        @keyframes signalSystemPulse {
          0% {
            transform: translate(-50%, -50%) scale(0.78);
            opacity: 0;
          }
          24% {
            opacity: 0.38;
          }
          100% {
            transform: translate(-50%, -50%) scale(1.26);
            opacity: 0;
          }
        }

        @keyframes signalDiscoveryRing {
          0%,
          100% {
            transform: scale(0.96);
            opacity: 0.32;
          }
          50% {
            transform: scale(1.04);
            opacity: 0.72;
          }
        }

        @keyframes signalDiscoveryBeam {
          0%,
          100% {
            transform: translate(-50%, -50%) rotate(-22deg);
            opacity: 0.48;
          }
          50% {
            transform: translate(-50%, -50%) rotate(22deg);
            opacity: 0.95;
          }
        }

        @keyframes signalDiscoveryPing {
          0%,
          100% {
            transform: scale(0.9);
            opacity: 0.5;
          }
          45% {
            transform: scale(1.35);
            opacity: 1;
          }
        }

        @keyframes signalDiscoveryTile {
          0%,
          100% {
            transform: translateY(0) rotate(12deg);
          }
          50% {
            transform: translateY(-6px) rotate(4deg);
          }
        }

        @keyframes signalConversationFloat {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-6px);
          }
        }

        @keyframes signalConversationTyping {
          0%,
          70%,
          100% {
            transform: translateY(0);
            opacity: 0.4;
          }
          35% {
            transform: translateY(-3px);
            opacity: 1;
          }
        }

        @keyframes signalConversationLine {
          0%,
          100% {
            transform: scaleX(0.82);
            opacity: 0.55;
          }
          50% {
            transform: scaleX(1);
            opacity: 0.95;
          }
        }

        @keyframes signalConversationOrb {
          0%,
          100% {
            transform: scale(0.92);
            opacity: 0.45;
          }
          50% {
            transform: scale(1.12);
            opacity: 0.9;
          }
        }

        @keyframes signalRadarRing {
          0%,
          100% {
            transform: scale(0.97);
            opacity: 0.28;
          }
          50% {
            transform: scale(1.03);
            opacity: 0.72;
          }
        }

        @keyframes signalRadarSweepLine {
          0%,
          100% {
            transform: translate(-50%, -100%) rotate(-22deg);
            opacity: 0.42;
          }
          50% {
            transform: translate(-50%, -100%) rotate(34deg);
            opacity: 0.95;
          }
        }

        @keyframes signalRadarBlip {
          0%,
          100% {
            transform: translateX(-50%) scale(0.84);
            opacity: 0.4;
          }
          45% {
            transform: translateX(-50%) scale(1.28);
            opacity: 1;
          }
        }

        @keyframes signalRadarNode {
          0%,
          100% {
            transform: translateY(0) rotate(0deg);
            opacity: 0.52;
          }
          50% {
            transform: translateY(-5px) rotate(8deg);
            opacity: 0.92;
          }
        }

        @keyframes radarDots {
          0%,
          80%,
          100% {
            opacity: 0.18;
          }
          40% {
            opacity: 1;
          }
        }
      `}</style>

      <ContactModal
        isOpen={showContactForm}
        onClose={() => setShowContactForm(false)}
      />
    </>
  );
}
